import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  RectangleProps,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Table } from "lucide-react";
import { Switch } from "../../UI/Switch.tsx";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  MUTED_INK,
  SURFACE,
} from "../../UI/Chart/chartStyle.ts";
import { SalesGranularity, SalesPeriod } from "./salesPeriod.ts";
import {
  SalesTrend,
  SalesTrendBucket,
  SalesTrendMetric,
  SalesTrendSeries,
  SalesTrendSplit,
} from "./salesTrend.ts";
import {
  formatBucket,
  formatBucketTick,
  formatCompactEuros,
  formatCount,
  formatDay,
  formatEuros,
} from "./salesFormat.ts";
import { PromotionInPeriod, promotionsActiveOn } from "./salesPromotions.ts";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import {
  EmptyStatistics,
  LoadingBlock,
  StatisticsCard,
} from "./StatisticsCard.tsx";
import { ChoiceControl } from "./ChoiceControl.tsx";
import { CsvButton } from "./CsvButton.tsx";
import { centsForCsv, downloadSalesCsv, toCsv } from "./salesCsv.ts";

const HOVERED_BUCKET_WASH = "#f0efec";
const PREVIOUS_PERIOD_COLOR = MUTED_INK;
const CHART_HEIGHT = 280;
const PROMOTION_BAND_COLOR = "#94a3b8";
const PROMOTION_LABEL_COLOR = "#64748b";
const PROMOTION_LABEL_STEP = 13;
const TOP_CORNERS_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const NO_RADIUS: [number, number, number, number] = [0, 0, 0, 0];

const METRIC_OPTIONS = [
  { value: SalesTrendMetric.Sold, label: "Venduto" },
  { value: SalesTrendMetric.NetSold, label: "Netto" },
  { value: SalesTrendMetric.Collected, label: "Incassato" },
  { value: SalesTrendMetric.Orders, label: "Ordini" },
];

const SPLIT_OPTIONS = [
  { value: SalesTrendSplit.Gateway, label: "Gateway" },
  { value: SalesTrendSplit.PaymentMethod, label: "Metodo" },
  { value: SalesTrendSplit.Item, label: "Prodotto" },
  { value: SalesTrendSplit.Installments, label: "Rate" },
];

const GRANULARITY_OPTIONS = [
  { value: SalesGranularity.Day, label: "Giorno" },
  { value: SalesGranularity.Week, label: "Settimana" },
  { value: SalesGranularity.Month, label: "Mese" },
];

const METRIC_DESCRIPTIONS: Record<SalesTrendMetric, string> = {
  [SalesTrendMetric.Sold]: "Ordini conclusi nel giorno in cui sono stati fatti",
  [SalesTrendMetric.NetSold]:
    "Ordini conclusi al netto dei rimborsi, nel giorno in cui sono stati fatti",
  [SalesTrendMetric.Collected]:
    "Pagamenti nel giorno in cui sono arrivati, rate e saldi compresi",
  [SalesTrendMetric.Orders]: "Numero di ordini conclusi",
};

type ChartBucket = SalesTrendBucket & {
  bucketKey: string;
  tick: string;
  previousTotal: number | null;
  previousBucket: SalesTrendBucket | null;
  promotions: PromotionInPeriod[];
};

function bucketKeyOf(bucket: SalesTrendBucket): string {
  return String(bucket.start.valueOf());
}

function bucketKeyContaining(
  buckets: ChartBucket[],
  date: PromotionInPeriod["from"],
): string | undefined {
  return buckets.find(
    (bucket) => !date.isBefore(bucket.start) && !date.isAfter(bucket.end),
  )?.bucketKey;
}

function formatValue(value: number, metric: SalesTrendMetric): string {
  return metric === SalesTrendMetric.Orders
    ? formatCount(value)
    : formatEuros(value);
}

function formatAxisValue(value: number, metric: SalesTrendMetric): string {
  return metric === SalesTrendMetric.Orders
    ? formatCount(value)
    : formatCompactEuros(value);
}

function valueForCsv(value: number, metric: SalesTrendMetric): string | number {
  return metric === SalesTrendMetric.Orders ? value : centsForCsv(value);
}

function topSeriesKeyOf(
  bucket: SalesTrendBucket,
  series: SalesTrendSeries[],
): string | undefined {
  return [...series]
    .reverse()
    .find((each) => (bucket.valuesBySeries[each.key] ?? 0) > 0)?.key;
}

type SegmentProps = RectangleProps & { payload: ChartBucket };

function StackedSegment({
  seriesKey,
  series,
  ...segment
}: SegmentProps & { seriesKey: string; series: SalesTrendSeries[] }) {
  return (
    <Rectangle
      {...segment}
      radius={
        topSeriesKeyOf(segment.payload, series) === seriesKey
          ? TOP_CORNERS_RADIUS
          : NO_RADIUS
      }
    />
  );
}

function TrendTooltip({
  active,
  payload,
  series,
  metric,
  granularity,
  comparisonLabel,
}: TooltipProps<number, string> & {
  series: SalesTrendSeries[];
  metric: SalesTrendMetric;
  granularity: SalesGranularity;
  comparisonLabel: string;
}) {
  const bucket = payload?.[0]?.payload as ChartBucket | undefined;
  if (!active || !bucket) return null;
  const seriesWithValues = series.filter(
    (each) => (bucket.valuesBySeries[each.key] ?? 0) !== 0,
  );
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1 min-w-52">
      <span className="font-semibold text-gray-700">
        {formatBucket(bucket, granularity)}
      </span>
      {[...seriesWithValues].reverse().map((each) => (
        <span
          key={each.key}
          className="flex items-center justify-between gap-4"
        >
          <span className="flex items-center gap-1.5 text-gray-500">
            <span
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: each.color }}
            />
            {each.label}
          </span>
          <span className="font-medium text-gray-800">
            {formatValue(bucket.valuesBySeries[each.key], metric)}
          </span>
        </span>
      ))}
      <span className="flex items-center justify-between gap-4 border-t border-gray-100 pt-1">
        <span className="text-gray-500">Totale</span>
        <span className="font-semibold text-gray-800">
          {formatValue(bucket.total, metric)}
        </span>
      </span>
      {bucket.previousBucket && (
        <span className="flex items-center justify-between gap-4 text-gray-500">
          <span>
            {comparisonLabel} (
            {formatBucket(bucket.previousBucket, granularity)})
          </span>
          <span className="font-medium text-gray-700">
            {formatValue(bucket.previousBucket.total, metric)}
          </span>
        </span>
      )}
      {bucket.promotions.length > 0 && (
        <span className="border-t border-gray-100 pt-1 text-gray-500">
          Promozioni:{" "}
          {bucket.promotions
            .map(
              (promotion) => `${promotion.shortName} ${promotion.promo.name}`,
            )
            .join(", ")}
        </span>
      )}
    </div>
  );
}

function TrendTable({
  buckets,
  series,
  metric,
  granularity,
  comparisonLabel,
  hasComparison,
}: {
  buckets: ChartBucket[];
  series: SalesTrendSeries[];
  metric: SalesTrendMetric;
  granularity: SalesGranularity;
  comparisonLabel: string;
  hasComparison: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-4 font-medium">Periodo</th>
            {series.map((each) => (
              <th key={each.key} className="py-2 pr-4 font-medium text-right">
                {each.label}
              </th>
            ))}
            <th className="py-2 pr-4 font-medium text-right">Totale</th>
            {hasComparison && (
              <th className="py-2 font-medium text-right">{comparisonLabel}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {buckets
            .filter((bucket) => bucket.total !== 0 || bucket.previousTotal)
            .map((bucket) => (
              <tr
                key={bucket.start.valueOf()}
                className="border-b border-gray-100 text-gray-700"
              >
                <td className="py-1.5 pr-4 whitespace-nowrap">
                  {formatBucket(bucket, granularity)}
                </td>
                {series.map((each) => (
                  <td key={each.key} className="py-1.5 pr-4 text-right">
                    {formatValue(bucket.valuesBySeries[each.key] ?? 0, metric)}
                  </td>
                ))}
                <td className="py-1.5 pr-4 text-right font-medium">
                  {formatValue(bucket.total, metric)}
                </td>
                {hasComparison && (
                  <td className="py-1.5 text-right text-gray-500">
                    {bucket.previousTotal === null
                      ? "—"
                      : formatValue(bucket.previousTotal, metric)}
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function PromotionsList({
  promotions,
  describeItemsOf,
}: {
  promotions: PromotionInPeriod[];
  describeItemsOf: (promotion: PromotionInPeriod) => string;
}) {
  return (
    <div className="flex flex-col gap-1 border-t border-gray-100 pt-2">
      <span className="text-xs font-medium text-gray-600">
        Promozioni attive nel periodo
      </span>
      <ul className="flex flex-col gap-1">
        {promotions.map((promotion) => (
          <li
            key={promotion.promo._id}
            className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-600"
          >
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
              {promotion.shortName}
            </span>
            <span className="font-medium text-gray-800">
              {promotion.promo.name}
            </span>
            <span>
              {formatDay(promotion.from)} – {formatDay(promotion.to)}
            </span>
            <span className="text-gray-500">
              · {describeItemsOf(promotion)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SalesTrendCard({
  trend,
  previousBuckets,
  comparisonLabel,
  promotions,
  describePromotionItems,
  metric,
  split,
  granularity,
  period,
  isLoading,
  onChangeMetric,
  onChangeSplit,
  separatesHeyLightTerms,
  onChangeSeparatesHeyLightTerms,
  onChangeGranularity,
}: {
  trend: SalesTrend;
  previousBuckets: SalesTrendBucket[];
  comparisonLabel: string;
  promotions: PromotionInPeriod[];
  describePromotionItems: (promotion: PromotionInPeriod) => string;
  metric: SalesTrendMetric;
  split: SalesTrendSplit;
  granularity: SalesGranularity;
  period: SalesPeriod;
  isLoading: boolean;
  onChangeMetric: (metric: SalesTrendMetric) => void;
  onChangeSplit: (split: SalesTrendSplit) => void;
  separatesHeyLightTerms: boolean;
  onChangeSeparatesHeyLightTerms: (separatesHeyLightTerms: boolean) => void;
  onChangeGranularity: (granularity: SalesGranularity) => void;
}) {
  const [isShowingTable, setIsShowingTable] = useState(false);
  const hasComparison = previousBuckets.length > 0;
  const chartBuckets: ChartBucket[] = trend.buckets.map((bucket, index) => ({
    ...bucket,
    bucketKey: bucketKeyOf(bucket),
    tick: formatBucketTick(bucket.start, granularity),
    previousTotal: previousBuckets[index]?.total ?? null,
    previousBucket: previousBuckets[index] ?? null,
    promotions: promotionsActiveOn(promotions, bucket),
  }));
  const tickByBucketKey = new Map(
    chartBuckets.map((bucket) => [bucket.bucketKey, bucket.tick]),
  );
  const hasValues = chartBuckets.some(
    (bucket) => bucket.total !== 0 || bucket.previousTotal,
  );

  const exportCsv = () =>
    downloadSalesCsv({
      section: "andamento",
      period,
      csv: toCsv(chartBuckets, [
        {
          header: "Periodo",
          valueOf: (bucket) => formatBucket(bucket, granularity),
        },
        ...trend.series.map((each) => ({
          header: each.label,
          valueOf: (bucket: ChartBucket) =>
            valueForCsv(bucket.valuesBySeries[each.key] ?? 0, metric),
        })),
        {
          header: "Totale",
          valueOf: (bucket) => valueForCsv(bucket.total, metric),
        },
        ...(hasComparison
          ? [
              {
                header: comparisonLabel,
                valueOf: (bucket: ChartBucket) =>
                  bucket.previousTotal === null
                    ? null
                    : valueForCsv(bucket.previousTotal, metric),
              },
            ]
          : []),
        {
          header: "Promozioni attive",
          valueOf: (bucket) =>
            bucket.promotions
              .map((promotion) => promotion.promo.name)
              .join(", "),
        },
      ]),
    });

  return (
    <StatisticsCard
      title="Andamento vendite"
      description={METRIC_DESCRIPTIONS[metric]}
      info={SALES_DEFINITIONS.trend}
      actions={
        <>
          <ChoiceControl
            label="Valore mostrato"
            options={METRIC_OPTIONS}
            value={metric}
            onChange={onChangeMetric}
          />
          <ChoiceControl
            label="Suddivisione"
            options={SPLIT_OPTIONS}
            value={split}
            onChange={onChangeSplit}
          />
          {split === SalesTrendSplit.Installments && (
            <Switch
              label="Separa le durate HeyLight"
              checked={separatesHeyLightTerms}
              onChange={onChangeSeparatesHeyLightTerms}
            />
          )}
          <ChoiceControl
            label="Raggruppamento"
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={onChangeGranularity}
          />
        </>
      }
    >
      {isLoading ? (
        <LoadingBlock className="h-72 w-full" />
      ) : !hasValues ? (
        <EmptyStatistics>
          Nessuna vendita nel periodo con i filtri selezionati
        </EmptyStatistics>
      ) : (
        <div className="flex flex-col gap-3">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart
              data={chartBuckets}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={GRID_COLOR} />
              {promotions.map((promotion, index) => (
                <ReferenceArea
                  key={promotion.promo._id}
                  x1={bucketKeyContaining(chartBuckets, promotion.from)}
                  x2={bucketKeyContaining(chartBuckets, promotion.to)}
                  fill={PROMOTION_BAND_COLOR}
                  fillOpacity={0.14}
                  ifOverflow="visible"
                  label={{
                    value: promotion.shortName,
                    position: "insideTopLeft",
                    fill: PROMOTION_LABEL_COLOR,
                    fontSize: 11,
                    fontWeight: 600,
                    dy: index * PROMOTION_LABEL_STEP,
                  }}
                />
              ))}
              <XAxis
                dataKey="bucketKey"
                tickFormatter={(bucketKey: string) =>
                  tickByBucketKey.get(bucketKey) ?? ""
                }
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: AXIS_COLOR }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                width={64}
                tick={AXIS_TICK}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                tickFormatter={(value: number) =>
                  formatAxisValue(value, metric)
                }
              />
              <Tooltip
                content={
                  <TrendTooltip
                    series={trend.series}
                    metric={metric}
                    granularity={granularity}
                    comparisonLabel={comparisonLabel}
                  />
                }
                cursor={{ fill: HOVERED_BUCKET_WASH }}
              />
              {trend.series.map((each) => (
                <Bar
                  key={each.key}
                  dataKey={(bucket: ChartBucket) =>
                    bucket.valuesBySeries[each.key] ?? 0
                  }
                  name={each.label}
                  stackId="sales"
                  fill={each.color}
                  stroke={SURFACE}
                  strokeWidth={2}
                  maxBarSize={40}
                  isAnimationActive={false}
                  shape={(shapeProps: unknown) => (
                    <StackedSegment
                      {...(shapeProps as SegmentProps)}
                      seriesKey={each.key}
                      series={trend.series}
                    />
                  )}
                />
              ))}
              {hasComparison && (
                <Line
                  dataKey="previousTotal"
                  name={comparisonLabel}
                  stroke={PREVIOUS_PERIOD_COLOR}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: PREVIOUS_PERIOD_COLOR,
                    stroke: SURFACE,
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {trend.series.map((each) => (
                <ChartLegendItem
                  key={each.key}
                  color={each.color}
                  label={each.label}
                  shape="block"
                />
              ))}
              {hasComparison && (
                <span className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span
                    className="w-4 border-t-2 border-dashed"
                    style={{ borderColor: PREVIOUS_PERIOD_COLOR }}
                  />
                  {comparisonLabel}
                </span>
              )}
              {promotions.length > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span
                    className="h-3 w-3 rounded-sm"
                    style={{
                      backgroundColor: PROMOTION_BAND_COLOR,
                      opacity: 0.35,
                    }}
                  />
                  Promozioni
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsShowingTable((isShowing) => !isShowing)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <Table className="w-3.5 h-3.5" aria-hidden />
                {isShowingTable ? "Nascondi tabella" : "Mostra tabella"}
              </button>
              <CsvButton onClick={exportCsv} />
            </div>
          </div>
          {promotions.length > 0 && (
            <PromotionsList
              promotions={promotions}
              describeItemsOf={describePromotionItems}
            />
          )}
          {isShowingTable && (
            <TrendTable
              buckets={chartBuckets}
              series={trend.series}
              metric={metric}
              granularity={granularity}
              comparisonLabel={comparisonLabel}
              hasComparison={hasComparison}
            />
          )}
        </div>
      )}
    </StatisticsCard>
  );
}
